import { createContext, useMemo, useState } from "react";
import { createTheme, Theme, ThemeOptions } from "@mui/material/styles";
import { mixHex } from "./shared/design/contrast";
import { getActiveLook, setActiveLook } from "./shared/design/activeLook";
import { AdminLook, LOOKS, LookTokens } from "./shared/design/looks";

type Shade = 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;

type ColorScale = Record<Shade, string>;

export type Tokens = {
  grey: ColorScale;
  primary: ColorScale;
  greenAccent: ColorScale;
  redAccent: ColorScale;
  blueAccent: ColorScale;
  /**
   * Amber/orange ramp used for "pending" / "warning" states — booking row
   * status, dashboard chart series, schedule-conflict banners. Mirrors the
   * inversion convention of the other accents (dark/light flip 100 and 900,
   * keep the saturated 500 the same in both modes).
   */
  warningAccent: ColorScale;
};

type ThemeMode = "light" | "dark";

/** The colour ramps before the looks (`classic`). */
const classicTokens = (mode: ThemeMode): Tokens => ({
  ...(mode === "dark"
    ? {
        grey: {
          // Aligned to Figma `Gray-500` (#A2A1A8) at the 400/500 step.
          // Keeps the 100→900 ramp shape so existing usages (text, borders,
          // dividers) carry over visually without per-callsite tweaks.
          100: "#e6e6e9",
          200: "#cbcbd0",
          300: "#b6b5bb",
          400: "#A2A1A8",
          500: "#85858c",
          600: "#5e5d63",
          700: "#3f3f44",
          800: "#27272a",
          900: "#16151c"
        },
        primary: {
          // Reseeded from Figma `Dark-500` (#16151C) — the chrome surface
          // color. 400 is one shade up (cards/panels on bg). 100 light-text
          // anchor stays close to white.
          100: "#d0d0d4",
          200: "#a0a0a8",
          300: "#71707d",
          400: "#23222b",
          500: "#16151C",
          600: "#121118",
          700: "#0e0e13",
          800: "#0a090e",
          900: "#050508"
        },
        greenAccent: {
          100: "#dbf5ee",
          200: "#b7ebde",
          300: "#94e2cd",
          400: "#70d8bd",
          500: "#4cceac",
          600: "#3da58a",
          700: "#2e7c67",
          800: "#1e5245",
          900: "#0f2922"
        },
        redAccent: {
          100: "#f8dcdb",
          200: "#f1b9b7",
          300: "#e99592",
          400: "#e2726e",
          500: "#db4f4a",
          600: "#af3f3b",
          700: "#832f2c",
          800: "#58201e",
          900: "#2c100f"
        },
        blueAccent: {
          // Brand blue — Figma uses #0075FF as the primary CTA + active
          // sidebar pill. Pinned at 500 so token consumers (`blueAccent[500]`)
          // get the brand color directly. Surrounding ramp derived to keep
          // hover (600) / hover-light (400) reasonable.
          100: "#e6f1ff",
          200: "#b3d3ff",
          300: "#80b6ff",
          400: "#3392ff",
          500: "#0075FF",
          600: "#005ecc",
          700: "#004599",
          800: "#002d66",
          900: "#001633"
        },
        warningAccent: {
          100: "#fff5e0",
          200: "#ffe6b3",
          300: "#ffd180",
          400: "#ffbd4d",
          500: "#FFA500",
          600: "#cc8400",
          700: "#996300",
          800: "#664200",
          900: "#332100"
        }
      }
    : {
        grey: {
          100: "#16151c",
          200: "#27272a",
          300: "#3f3f44",
          400: "#5e5d63",
          500: "#85858c",
          600: "#A2A1A8",
          700: "#b6b5bb",
          800: "#cbcbd0",
          900: "#e6e6e9"
        },
        primary: {
          // Light-mode primary ramp. 400 = page surface (#f2f0f0 light grey),
          // 500 = "card / elevated panel" (white) — used by every legacy
          // dark-only callsite that bgcolor's a panel with `primary[500]`.
          // Sidebar/topbar pick their own mode-aware bg explicitly via
          // `primary[400]` so this reset doesn't lighten the chrome.
          100: "#050508",
          200: "#0a090e",
          300: "#0e0e13",
          400: "#f2f0f0",
          500: "#ffffff",
          600: "#e8e6e6",
          700: "#71707d",
          800: "#a0a0a8",
          900: "#d0d0d4"
        },
        greenAccent: {
          100: "#0f2922",
          200: "#1e5245",
          300: "#2e7c67",
          400: "#3da58a",
          500: "#4cceac",
          600: "#70d8bd",
          700: "#94e2cd",
          800: "#b7ebde",
          900: "#dbf5ee"
        },
        redAccent: {
          100: "#2c100f",
          200: "#58201e",
          300: "#832f2c",
          400: "#af3f3b",
          500: "#db4f4a",
          600: "#e2726e",
          700: "#e99592",
          800: "#f1b9b7",
          900: "#f8dcdb"
        },
        blueAccent: {
          100: "#001633",
          200: "#002d66",
          300: "#004599",
          400: "#005ecc",
          500: "#0075FF",
          600: "#3392ff",
          700: "#80b6ff",
          800: "#b3d3ff",
          900: "#e6f1ff"
        },
        warningAccent: {
          100: "#332100",
          200: "#664200",
          300: "#996300",
          400: "#cc8400",
          500: "#FFA500",
          600: "#ffbd4d",
          700: "#ffd180",
          800: "#ffe6b3",
          900: "#fff5e0"
        }
      })
});

/**
 * Old `tokens(mode)` call sites (about 300) follow the active look, set by
 * `useMode(look)` before the theme is built: a screen that re-renders on
 * the new theme reads the new ramps without being touched.
 */
export { setActiveLook };

/** 100 leans to `fg`, 500 = base, 900 leans to `bg` — the classic ramps' shape in both modes. */
function ramp(base: string, t: LookTokens): ColorScale {
  const out = {} as ColorScale;
  ([100, 200, 300, 400, 500, 600, 700, 800, 900] as Shade[]).forEach((k) => {
    const i = k / 100;
    out[k] = i === 5 ? base : i < 5 ? mixHex(base, t.fg, ((5 - i) / 5) * 0.85) : mixHex(base, t.bg, ((i - 5) / 4) * 0.9);
  });
  return out;
}

function lookTokens(t: LookTokens): Tokens {
  const grey = {} as ColorScale;
  ([100, 200, 300, 400, 500, 600, 700, 800, 900] as Shade[]).forEach((k, i) => {
    grey[k] = i === 0 ? t.fg : mixHex(t.fg, t.bg, i / 9);
  });
  return {
    grey,
    // Same roles as classic: 400 = page / chrome, 500 = card, 600 = quiet surface.
    primary: {
      100: t.fg,
      200: mixHex(t.fg, t.muted, 0.5),
      300: t.muted,
      400: t.bg,
      500: t.card,
      600: t.quietBg,
      700: t.muted,
      800: t.borderStrong,
      900: t.border,
    },
    greenAccent: ramp(t.okFg, t),
    redAccent: ramp(t.dangerFg, t),
    blueAccent: ramp(t.primary, t),
    warningAccent: ramp(t.warnFg, t),
  };
}

export const tokens = (mode: ThemeMode, look: AdminLook = getActiveLook()): Tokens =>
  look === "classic" ? classicTokens(mode) : lookTokens(LOOKS[look][mode]);

type Shape = { radius: number; radiusSm: number; edge?: string };

/** A look's theme: palette, font and radius from its tokens; same component rules as classic. */
const lookSettings = (mode: ThemeMode, look: Exclude<AdminLook, "classic">): ThemeOptions => {
  const L = LOOKS[look];
  const t = L[mode];
  const colors = lookTokens(t);
  const base = classicSettings(mode, { radius: L.radius, radiusSm: L.radiusSm, edge: t.border });
  const font = (fontSize: number) => ({ fontFamily: L.font, fontSize });
  return {
    ...base,
    palette: {
      mode,
      primary: { main: t.primary, contrastText: t.onPrimary },
      secondary: { main: t.okFg },
      neutral: { dark: colors.grey[700], main: colors.grey[500], light: colors.grey[100] },
      background: { default: t.bg, paper: t.card },
      text: { primary: t.fg, secondary: t.muted },
      divider: t.border,
      error: { main: t.dangerFg },
      warning: { main: t.warnFg },
      success: { main: t.okFg },
    } as ThemeOptions["palette"],
    typography: {
      fontFamily: L.font,
      fontSize: 12,
      h1: font(40),
      h2: font(32),
      h3: font(24),
      h4: font(20),
      h5: font(16),
      h6: font(14),
    },
    shape: { borderRadius: L.radiusSm },
  };
};

export const themeSettings = (mode: ThemeMode, look: AdminLook = "classic"): ThemeOptions =>
  look === "classic" ? classicSettings(mode) : lookSettings(mode, look);

/** Today's theme, unchanged; `shape` only re-radiuses / re-borders it for a look. */
const classicSettings = (mode: ThemeMode, shape: Shape = { radius: 10, radiusSm: 8 }): ThemeOptions => {
  const colors = classicTokens(mode);
  const edge = (t: any) =>
    shape.edge ?? (t.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)");
  return {
    palette: {
      mode,
      ...(mode === "dark"
        ? {
            // `palette.primary.main` = the brand color used by MUI for
            // `<IconButton color="primary">`, `<Button color="primary">`,
            // active tab indicator, link color, etc. Was `colors.primary[500]`
            // = #16151C (near-black) which made primary IconButtons
            // effectively invisible on dark cards. Pin to brand blue.
            primary: { main: colors.blueAccent[500] },
            secondary: { main: colors.greenAccent[500] },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100]
            },
            // Page bg is one shade UP from chrome (sidebar/header at
            // primary[500] = #16151C) so cards on the page read as
            // elevated. Matches the Figma layering.
            background: { default: colors.primary[400] }
          }
        : {
            primary: { main: colors.blueAccent[500] },
            secondary: { main: colors.greenAccent[500] },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100]
            },
            background: { default: "#fcfcfc" }
          })
    },
    typography: {
      fontFamily: ["Lexend", "sans-serif"].join(","),
      fontSize: 12,
      h1: {
        fontFamily: ["Lexend", "sans-serif"].join(","),
        fontSize: 40
      },
      h2: {
        fontFamily: ["Lexend", "sans-serif"].join(","),
        fontSize: 32
      },
      h3: {
        fontFamily: ["Lexend", "sans-serif"].join(","),
        fontSize: 24
      },
      h4: {
        fontFamily: ["Lexend", "sans-serif"].join(","),
        fontSize: 20
      },
      h5: {
        fontFamily: ["Lexend", "sans-serif"].join(","),
        fontSize: 16
      },
      h6: {
        fontFamily: ["Lexend", "sans-serif"].join(","),
        fontSize: 14
      }
    },
    // Component-level shape overrides — radius/padding/weight tuned
    // against the Figma design system. Concentrated here so individual
    // call sites stay clean (no per-component sx={{ borderRadius: ... }}).
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            // Lexend SemiBold for action labels matches the Figma
            // typography ramp; the slightly larger 8px radius softens
            // the legacy MUI 4px corners.
            textTransform: "none",
            fontWeight: 600,
            borderRadius: shape.radiusSm,
            letterSpacing: 0,
          },
        },
        defaultProps: {
          disableElevation: true,
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ ownerState, theme: t }: any) => ({
            // Card surfaces use 10px radius — same family as buttons,
            // marginally larger so cards visually anchor below buttons.
            // Drop the default elevation gradient and replace with a
            // mode-aware 1px border. Without it, light-mode cards
            // disappeared into the grey page bg and dark-mode cards
            // had no visible edge against the page surface.
            borderRadius: shape.radius,
            backgroundImage: "none",
            // Skip the border on already-bordered variants (variant=outlined)
            // and on chrome surfaces like the dropdown Menu (rendered as
            // floating Paper) so they don't double-up.
            ...(ownerState?.variant !== "outlined" && {
              border: `1px solid ${edge(t)}`,
            }),
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme: t }: any) => ({
            borderRadius: shape.radius,
            backgroundImage: "none",
            border: `1px solid ${edge(t)}`,
          }),
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: "outlined" as const,
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: shape.radiusSm,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: shape.radiusSm,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: ({ theme: t }: any) => ({
            backgroundColor: t.palette.primary.main,
          }),
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme: t }: any) => ({
            textTransform: "none",
            fontWeight: 600,
            color: t.palette.text.secondary,
            "&.Mui-selected": {
              color: t.palette.primary.main,
            },
          }),
        },
      },
    },
  };
};

export type ColorModeContextType = {
  toggleColorMode: () => void;
};

export const ColorModeContext = createContext<ColorModeContextType>({
  toggleColorMode: () => {}
});

/** Persists the operator's mode choice across reloads. The Figma design
 *  is light-first; new sessions default to light. Anyone who toggles to
 *  dark keeps it on next visit. */
const MODE_STORAGE_KEY = "cb-admin-color-mode";

function readStoredMode(): ThemeMode {
  if (typeof window === "undefined") return "light";
  try {
    const v = window.localStorage.getItem(MODE_STORAGE_KEY);
    return v === "dark" || v === "light" ? v : "light";
  } catch {
    return "light"; // storage blocked (private mode / site data off)
  }
}

export const useMode = (look: AdminLook = "classic"): [Theme, ColorModeContextType] => {
  const [mode, setMode] = useState<ThemeMode>(readStoredMode);
  const colorMode = useMemo<ColorModeContextType>(
    () => ({
      toggleColorMode: () =>
        setMode((prevMode) => {
          const next = prevMode === "dark" ? "light" : "dark";
          try {
            window.localStorage.setItem(MODE_STORAGE_KEY, next);
          } catch {
            /* storage may be blocked (private mode); falls back to in-memory only */
          }
          return next;
        }),
    }),
    [],
  );
  const theme = useMemo(() => {
    setActiveLook(look, mode);
    return createTheme(themeSettings(mode, look));
  }, [mode, look]);
  return [theme, colorMode];
};
